import React, { useState, useEffect } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { addCoin } from "../services/coin_service.tsx";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../services/authContext_service.tsx";

// slike
const background = require("../assets/home/background.png");
const details = require("../assets/home/details.png");
const woman = require("../assets/home/woman.png");
const obverseCoin = require("../assets/home/avers coin.png");
const reverseCoin = require("../assets/home/revers coin.png");
const submitButton = require("../assets/home/submit example button.png");
const field = require("../assets/home/field.png");

// NAV
const homeBtn = require("../assets/home button.png");
const yourCoinsBtn = require("../assets/your coins.png");
const numStoriesBtn = require("../assets/num stories button.png");
const signOutBtn = require("../assets/sign out button.png");

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const isTablet = useMediaQuery("(max-width:1200px)");
  const isSmall = useMediaQuery("(max-width:700px)");

  const [formData, setFormData] = useState({
    name: "",
    location: "",
    material: "",
    weight: "",
    diameter: "",
    condition: "",
    front_image: null as File | null,
    back_image: null as File | null,
  });

  const [fileNames, setFileNames] = useState({
    front: "",
    back: "",
  });

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  useEffect(() => {
    const images = [
      background,
      details,
      woman,
      obverseCoin,
      reverseCoin,
      submitButton,
      field,
      homeBtn,
      yourCoinsBtn,
      numStoriesBtn,
      signOutBtn
    ];

    let loaded = 0;

    images.forEach((src) => {
      const img = new Image();
      img.src = src;

      const done = () => {
        loaded++;
        if (loaded === images.length) {
          setImagesLoaded(true);
        }
      };

      img.onload = done;
      img.onerror = done;
    });
  }, []);

  useEffect(() => {
    setLoading(false);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const file = e.target.files[0];

      setFormData({ ...formData, [e.target.name]: file });

      setFileNames((prev) => ({
        ...prev,
        [e.target.name === "front_image" ? "front" : "back"]: file.name,
      }));
    }
  };

  const validateForm = () => {
    if (
      !formData.name ||
      !formData.location ||
      !formData.material ||
      !formData.weight ||
      !formData.diameter ||
      !formData.condition
    ) {
      return "All fields must be filled.";
    }

    if (!formData.front_image || !formData.back_image) {
      return "Both obverse and reverse images are required.";
    }

    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      setSuccess(null);
      return;
    }

    setError(null);

    const token = localStorage.getItem("accessToken");
    if (!token) {
      setError("You are not authenticated.");
      navigate("/login");
      return;
    }

    try {
      const formDataToSend = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        if (value) formDataToSend.append(key, value as any);
      });

      await addCoin(formDataToSend, token);

      setSuccess("Coin successfully added!");
      setError(null);

      setFormData({
        name: "",
        location: "",
        material: "",
        weight: "",
        diameter: "",
        condition: "",
        front_image: null,
        back_image: null,
      });

      setFileNames({ front: "", back: "" });

      setTimeout(() => {
        setSuccess(null);
      }, 3000);

    } catch (err: any) {
      setError(err.message || "Error while adding coin.");
      setSuccess(null);
    }
  };

  const renderField = (name: string, placeholder: string) => (
    <Box sx={{ position: "relative", width: isSmall ? "160px" : "230px" }}>
      <img src={field} style={{ width: "100%" }} />

      <input
        name={name}
        value={(formData as any)[name]}
        onChange={handleChange}
        placeholder={placeholder}
        autoComplete="off"
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "80%",
          background: "transparent",
          border: "none",
          outline: "none",
          color: "#fff",
          fontSize: "12px",
          textAlign: "center",
        }}
      />
    </Box>
  );

  const renderUploadField = (name: string, label: string, fileName: string) => (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          position: "relative",
          width: isSmall ? "180px" : "220px",
          cursor: "pointer",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "scale(1.05)",
            filter: "drop-shadow(0 0 15px #ff00cc)",
          },
        }}
      >
        <img src={field} style={{ width: "100%" }} />

        <input hidden type="file" name={name} onChange={handleFileChange} id={name} />

        <label
          htmlFor={name}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
          }}
        >
          <Typography sx={{ color: "#fff", fontSize: "14px" }}>
            {label}
          </Typography>
        </label>
      </Box>

      {fileName && (
        <Typography sx={{ color: "#ccc", fontSize: "11px", textAlign: "center" }}>
          {fileName}
        </Typography>
      )}
    </Box>
  );

  if (loading || !imagesLoaded) {
    return (
      <Box sx={{ width: "100vw", height: "100vh", display: "flex", justifyContent: "center", alignItems: "center", background: "black" }}>
        <CircularProgress size={80} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: "100vh",
        overflowY: "auto",
        overflowX: "hidden",
        paddingBottom: "80px"
      }}
    >
      <img src={background} style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute" }} />
      <img src={details} style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute" }} />

      {/* NAV */}
      <Box sx={{ position: "absolute", top: "15px", width: "100%", display: "flex", justifyContent: "center", gap: isSmall ? 1 : 4, zIndex: 5 }}>
        <img src={homeBtn} style={{ width: isSmall ? "90px" : "140px", cursor: "pointer" }} onClick={() => navigate("/home")} />
        <img src={yourCoinsBtn} style={{ width: isSmall ? "90px" : "140px", cursor: "pointer" }} onClick={() => navigate("/view-coins")} />
        <img src={numStoriesBtn} style={{ width: isSmall ? "90px" : "140px", cursor: "pointer" }} onClick={() => navigate("/home")} />
        <img src={signOutBtn} style={{ width: isSmall ? "90px" : "140px", cursor: "pointer" }} onClick={logout} />
      </Box>

      {/* 🔥 ŽENE DOLE U ĆOŠKU */}
      {!isTablet && (
        <>
          <Box sx={{ position: "absolute", left: "-120px", bottom: "0px", height: "70%" }}>
            <img src={woman} style={{ height: "100%", opacity: 0.6 }} />
          </Box>

          <Box sx={{ position: "absolute", right: "-120px", bottom: "0px", height: "70%", transform: "scaleX(-1)" }}>
            <img src={woman} style={{ height: "100%", opacity: 0.6 }} />
          </Box>
        </>
      )}

      <Box
        sx={{
          position: "absolute",
          top: "52%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isTablet ? "95%" : "1050px",
          textAlign: "center"
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", gap: isSmall ? "40px" : "120px", mb: 2 }}>
          <Box>
            <img src={obverseCoin} style={{ width: isSmall ? "150px" : "200px" }} />
            {renderUploadField("front_image", "Upload Obverse", fileNames.front)}
          </Box>

          <Box>
            <img src={reverseCoin} style={{ width: isSmall ? "150px" : "200px" }} />
            {renderUploadField("back_image", "Upload Reverse", fileNames.back)}
          </Box>
        </Box>

        <Box onClick={handleSubmit} sx={{ mb: 2, cursor: "pointer" }}>
          <img src={submitButton} style={{ width: isSmall ? "200px" : "280px" }} />
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 2 }}>
          {renderField("location", "Location of Coins")}
          {renderField("material", "Material")}
          {renderField("weight", "Weight")}
          {renderField("diameter", "Diameter")}
          {renderField("condition", "Condition")}
          {renderField("name", "Name Your Example")}
        </Box>

        {/* 🔥 ERROR / SUCCESS BOX */}
        {error && (
          <Box sx={{ mt: 2, background: "rgba(0,0,0,0.7)", padding: "10px 20px", borderRadius: "10px", display: "inline-block" }}>
            <Typography sx={{ color: "#ff4d6d" }}>{error}</Typography>
          </Box>
        )}

        {success && (
          <Box sx={{ mt: 2, background: "rgba(0,0,0,0.7)", padding: "10px 20px", borderRadius: "10px", display: "inline-block" }}>
            <Typography sx={{ color: "#00ff99" }}>{success}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Home;